import { useState, useEffect, useRef } from 'react';
import { Client } from '@twilio/conversations';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Close from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import {
  fetchTwilioTokenWithChatId,
  sendMessage,
  markAsRead,
} from '../../../../actions/messaging';
import MessageList from './MessageList';
import MessageForm from './MessageForm';

function TextChat({
  fetchTwilioTokenWithChatId: fetchTwilioTokenWithChatIdAction,
  sendMessage: sendMessageAction,
  token = {},
  open,
  toggle,
  timezone,
  receivers,
  appointment = {},
  markAsRead: markAsReadAction,
}) {
  const [messages, setMessages] = useState([]);
  const [channel, setChannel] = useState(null);
  const [chat, setChat] = useState(null);
  const chatClientRef = useRef(null);
  const stateRef = useRef({ messages, channel, chat });
  const propsRef = useRef({
    token,
    open,
    toggle,
    appointment,
    markAsReadAction,
    sendMessageAction,
    fetchTwilioTokenWithChatIdAction,
  });
  stateRef.current = { messages, channel, chat };
  propsRef.current = {
    token,
    open,
    toggle,
    appointment,
    markAsReadAction,
    sendMessageAction,
    fetchTwilioTokenWithChatIdAction,
  };

  const addMessage = (message, mark = false) => {
    const messageData = {
      ...message,
      me: message.author === propsRef.current.token.identity,
    };
    setMessages(prev => {
      const next = [...prev, messageData];
      return next;
    });
    // Preserve class setState callback timing via microtask after state update.
    Promise.resolve().then(() => {
      if (!messageData.me && mark) {
        propsRef.current.markAsReadAction({
          id: stateRef.current.chat.id,
        });
      }
    });
    if (!propsRef.current.open) {
      propsRef.current.toggle();
    }
  };

  const setupChannel = nextChannel => {
    // Get Messages for a previously created channel
    nextChannel.getMessages().then(channelMessages => {
      const totalMessages = channelMessages.items.length;
      for (let message = 0; message < totalMessages; message += 1) {
        addMessage(channelMessages.items[message].state, false);
      }
    });

    nextChannel.on('messageAdded', ({ author, body, dateCreated }) => {
      addMessage({ author, body, timestamp: dateCreated }, true);
    });
  };

  const rejoinChannel = chatClient => {
    const {
      appointment: { chat: appointmentChat },
    } = propsRef.current;

    setChat(appointmentChat);
    propsRef.current.markAsReadAction({ id: appointmentChat.id });
    const promise = chatClient.getConversationBySid(appointmentChat.channel);
    promise
      .then(joinedChannel => {
        setChannel(joinedChannel);
        setupChannel(joinedChannel);
      })
      .catch(() => {});
  };

  const initChats = () => {
    const {
      appointment: { chat: appointmentChat },
    } = propsRef.current;
    const chatId = appointmentChat.id;

    propsRef.current.fetchTwilioTokenWithChatIdAction(
      chatId,
      tokenResponse => {
        const tokenData = tokenResponse.data;
        if (chatClientRef.current) {
          try {
            chatClientRef.current.removeAllListeners();
            chatClientRef.current.shutdown();
          } catch (shutdownError) {
            // ignore
          }
        }
        const chatClient = new Client(tokenData.id);
        chatClientRef.current = chatClient;
        chatClient.on('connectionError', () => {});

        chatClient.on('stateChanged', state => {
          if (state === 'initialized') {
            rejoinChannel(chatClient);
          }
        });
      },
    );
  };

  useEffect(() => {
    initChats();

    return () => {
      if (stateRef.current.channel) {
        stateRef.current.channel.removeAllListeners('messageAdded');
      }
      if (chatClientRef.current) {
        try {
          chatClientRef.current.removeAllListeners();
          chatClientRef.current.shutdown();
        } catch (error) {
          // Client may already be shut down.
        }
        chatClientRef.current = null;
      }
    };
    // Match class componentDidMount / WillUnmount lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNewMessage = text => {
    if (stateRef.current.channel) {
      propsRef.current.sendMessageAction({
        id: stateRef.current.chat.id,
        message: text,
      });
      stateRef.current.channel.sendMessage(text);
    }
  };

  return (
    <div className="chat-container">
      {open ? (
        <Close
          onClick={toggle}
          style={{
            position: 'absolute',
            top: '5px',
            right: '5px',
            cursor: 'pointer',
            color: 'white',
            width: '13px',
            height: '13px',
          }}
        />
      ) : (
        <ExpandLess
          onClick={toggle}
          style={{
            position: 'absolute',
            top: '5px',
            right: '5px',
            cursor: 'pointer',
            color: 'white',
            width: '16px',
            height: '16px',
          }}
        />
      )}
      <div className="chat-title">
        <p className="chat-title-text">
          {receivers
            .map(receiver => receiver.preferred_name_with_last_name)
            .join(', ')}
        </p>
      </div>
      <div className={open ? '' : 'close-message-box'}>
        <MessageList messages={messages} timezone={timezone} />
        <MessageForm
          connected_yn={channel !== null}
          onMessageSend={handleNewMessage}
        />
      </div>
    </div>
  );
}

TextChat.propTypes = {
  fetchTwilioTokenWithChatId: PropTypes.func.isRequired,
  sendMessage: PropTypes.func.isRequired,
  token: PropTypes.object,
  open: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  timezone: PropTypes.string.isRequired,
  receivers: PropTypes.array.isRequired,
  appointment: PropTypes.object,
  markAsRead: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    token: state.misc.token,
  };
}

export default connect(mapStateToProps, {
  fetchTwilioTokenWithChatId,
  sendMessage,
  markAsRead,
})(TextChat);
